/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Dialog, Grid, Typography } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  NoticeCriteriaGroupActivity,
  StatusCode,
} from '../../../../../../../../api/enumerations';
import {
  addToCriteriaGroup,
  removeFromCriteriaGroup,
  updateCriteriaGroup,
} from '../../../../../../../../api/theHive/criteriaGroups';
import {
  CriteriaData,
  DetailProps,
  TypologyAndCriteriaData,
} from '../../../../../../../../api/theHive/criteriaGroups/types';
import {
  createTypologyGroup,
  deleteTypologyGroup,
  updateTypologyGroup,
} from '../../../../../../../../api/theHive/typology';
import {
  BillingUnit,
  ReportType,
  TypologyCategory,
  TypologypropertyType,
  TypologyRuleKeyword,
} from '../../../../../../../../api/theHive/typology/enumerations';
import {
  EditTypologyGroupData,
  TypologyData,
  TypologyGroupItemData,
} from '../../../../../../../../api/theHive/typology/types';
import { SelectTextField } from '../../../../../../../../components/CustomInput';
import { WhiteCircularProgress } from '../../../../../../../../components/UI/Typography';
import { IconAddMS, IconEditMS } from '../../../../../../../../constants/icons';
import { selectGroupActivity } from '../../../../../../../../constants/selectOptions';
import { GlobalContext } from '../../../../../../../../context/global';
import useErrorMessage from '../../../../../../../../hooks/useErrorMessage';
import {
  CancelButton,
  SaveButton,
  SubsectionTitle,
  TitleMoldal,
} from '../../styles';
import {
  ALL_SIZES,
  EditableTypologyGroupFields,
  ITypologyModalProps,
  ServiceGroup,
  Typology,
  TypologyGroup,
} from '../../types';
import {
  convertServiceGroup,
  createEmptyService,
  createEmptyTypology,
} from '../../utils';
import CriteriaItem from '../AddTypologyModal/components/CriteriaItem';
import CriteriasDialog from '../AddTypologyModal/components/CriteriasDialog';
import ServiceItem from '../AddTypologyModal/components/ServiceItem';
import { useCriterias } from '../AddTypologyModal/hooks/useCriterias';
import {
  AddTypologyGroup,
  DialogContainer,
  StyledDialog,
  StyledPaper,
} from '../AddTypologyModal/styles';

interface EditGroupModalProps extends ITypologyModalProps {
  group: TypologyAndCriteriaData;
  open: boolean;
  handleClose: () => void;
}

export function EditGroupModal({
  group,
  fetchTypologyGroups,
  open,
  handleClose,
}: EditGroupModalProps): JSX.Element {
  const [isSaving, setIsSaving] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [serviceGroup, setServiceGroup] = useState<ServiceGroup>(
    convertServiceGroup(group)
  );

  // snapshot do estado original para o diff no salvar
  const originalCriteriaIds = useMemo(
    () => (group.criterias ?? []).map((criteria) => criteria.id),
    [group]
  );
  const originalServiceIds = useMemo(
    () => (group.typology_groups ?? []).map((service) => service.id),
    [group]
  );
  const grouping = group.typology_groups?.[0]?.grouping ?? 1;

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();
  const { notice } = useParams();
  const noticeId = parseInt(notice as string, 10);

  const {
    loadingError,
    defaultCriterias,
    loadingCriterias,
    getDefaultCriteriasCallback,
  } = useCriterias(serviceGroup.activity ?? NoticeCriteriaGroupActivity.SELECT);

  // Saved group criteria may not be in the default pool (fetched by activity),
  // so merge both sources by id to resolve each criterion's label/description.
  const criteriasLookup = useMemo(() => {
    const byId = new Map<number, CriteriaData>();
    defaultCriterias.forEach((criteria) => byId.set(criteria.id, criteria));
    (group.criterias ?? []).forEach((criteria) =>
      byId.set(criteria.id, criteria)
    );
    return Array.from(byId.values());
  }, [defaultCriterias, group]);

  const handleReset = (): void => setServiceGroup(convertServiceGroup(group));

  const closeDialog = (): void => {
    handleClose();
    handleReset();
  };

  const updateService = (
    serviceIndex: number,
    updater: (service: TypologyGroup) => TypologyGroup
  ): void =>
    setServiceGroup((prev) => ({
      ...prev,
      services: prev.services.map((service, i) =>
        i === serviceIndex ? updater(service) : service
      ),
    }));

  // ── grupo (conceito de espaço + critérios) ───────────
  const handleActivityChange = (value: NoticeCriteriaGroupActivity): void =>
    setServiceGroup((prev) => ({ ...prev, activity: value, criteriaIds: [] }));

  const handleCriteriaSelection = (ids: number[]): void =>
    setServiceGroup((prev) => ({ ...prev, criteriaIds: ids }));

  const handleDeleteCriteria = (id: number): void =>
    setServiceGroup((prev) => ({
      ...prev,
      criteriaIds: prev.criteriaIds.filter((item) => item !== id),
    }));

  // ── serviço ──────────────────────────────────────────
  const handleAddService = (): void =>
    setServiceGroup((prev) => ({
      ...prev,
      services: [...prev.services, createEmptyService()],
    }));

  const handleDeleteService = (serviceIndex: number): void =>
    setServiceGroup((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== serviceIndex),
    }));

  const handleServiceChange = <K extends keyof EditableTypologyGroupFields>(
    serviceIndex: number,
    field: K,
    value: EditableTypologyGroupFields[K]
  ): void =>
    updateService(serviceIndex, (service) => ({ ...service, [field]: value }));

  // ── tipo ─────────────────────────────────────────────
  const handleAddTypologyRow = (serviceIndex: number): void =>
    updateService(serviceIndex, (service) => ({
      ...service,
      typologies: [...service.typologies, createEmptyTypology()],
    }));

  const handleDeleteTypology = (
    serviceIndex: number,
    typologyIndex: number
  ): void =>
    updateService(serviceIndex, (service) => ({
      ...service,
      typologies: service.typologies.filter((_, i) => i !== typologyIndex),
    }));

  const handleTypologyChange = <K extends keyof Typology>(
    serviceIndex: number,
    typologyIndex: number,
    field: K,
    value: Typology[K]
  ): void =>
    updateService(serviceIndex, (service) => ({
      ...service,
      typologies: service.typologies.map((typology, i) =>
        i === typologyIndex ? { ...typology, [field]: value } : typology
      ),
    }));

  const isServiceValid = (service: TypologyGroup): boolean => {
    const idValid =
      !!service.category &&
      !!service.code.trim() &&
      !!service.name.trim() &&
      !!service.reportType &&
      !!service.billingUnit &&
      !!service.priceType &&
      service.price > 0;
    if (!idValid || service.typologies.length === 0) return false;

    return service.typologies.every((typology) => {
      if (typology.type === TypologypropertyType.SELECT) return false;
      if (typology.range === TypologyRuleKeyword.SELECT) return false;
      if (typology.range === ALL_SIZES) return true;
      if (!typology.area) return false;
      if (typology.range === TypologyRuleKeyword.BETWEEN) {
        return !!typology.area2;
      }
      return true;
    });
  };

  const isValid = useMemo(
    () =>
      serviceGroup.activity !== NoticeCriteriaGroupActivity.SELECT &&
      serviceGroup.criteriaIds.length > 0 &&
      serviceGroup.services.length > 0 &&
      serviceGroup.services.every(isServiceValid),
    [serviceGroup]
  );

  const buildTypology = (typology: Typology): TypologyData => {
    if (typology.range === ALL_SIZES) {
      return {
        real_estate_type: typology.type,
        rule_keyword: TypologyRuleKeyword.OVER,
        rule_first_value: 1,
      };
    }
    return {
      real_estate_type: typology.type,
      rule_keyword: typology.range as TypologyRuleKeyword,
      rule_first_value: Number(typology.area),
      ...(typology.range === TypologyRuleKeyword.BETWEEN && {
        rule_second_value: Number(typology.area2),
      }),
    };
  };

  const buildNewService = (service: TypologyGroup): TypologyGroupItemData => ({
    name: service.name,
    price: service.price,
    notice_id: noticeId,
    code: service.code,
    category: service.category as TypologyCategory,
    report_type: service.reportType as ReportType,
    billing_unit: service.billingUnit as BillingUnit,
    price_type: service.priceType,
    grouping,
    deadline_by_project: service.deadlineByProject,
    typologies: service.typologies.filter((t) => t.visible).map(buildTypology),

    ...(service.slaAlertDays > 0 && { sla_alert_days: service.slaAlertDays }),
    ...(!service.deadlineByProject &&
      service.slaLimitDays > 0 && { sla_limit_days: service.slaLimitDays }),
    ...(service.slaUseBusinessDay !== null && {
      sla_use_business_days: service.slaUseBusinessDay,
    }),
  });

  const buildExistingService = (
    service: TypologyGroup
  ): EditTypologyGroupData => ({
    name: service.name,
    price: service.price,
    code: service.code,
    category: service.category as TypologyCategory,
    report_type: service.reportType as ReportType,
    billing_unit: service.billingUnit as BillingUnit,
    price_type: service.priceType,
    deadline_by_project: service.deadlineByProject,
    typologies: service.typologies.filter((t) => t.visible).map(buildTypology),

    ...(service.slaAlertDays > 0 && { sla_alert_days: service.slaAlertDays }),
    ...(service.deadlineByProject
      ? { sla_limit_days: null }
      : service.slaLimitDays > 0 && { sla_limit_days: service.slaLimitDays }),
    ...(service.slaUseBusinessDay !== null && {
      sla_use_business_days: service.slaUseBusinessDay,
    }),
  });

  const ensureOk = (response: { detail: DetailProps['detail'] }): void => {
    if (response.detail.description)
      throw new Error(response.detail.description);
    if (response.detail.status_code !== StatusCode.OK) {
      throw new Error('Algo deu errado, tente novamente.');
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!isValid) {
      setSnackbarMessage('Preencha todos os campos obrigatórios do grupo.');
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }

    const groupId = serviceGroup.id;
    const editedServiceIds = serviceGroup.services.map((s) => s.id);

    const addedCriteria = serviceGroup.criteriaIds.filter(
      (id) => !originalCriteriaIds.includes(id)
    );
    const removedCriteria = originalCriteriaIds.filter(
      (id) => !serviceGroup.criteriaIds.includes(id)
    );
    const existingServices = serviceGroup.services.filter((s) =>
      originalServiceIds.includes(s.id)
    );
    const newServices = serviceGroup.services.filter(
      (s) => !originalServiceIds.includes(s.id)
    );
    const removedServiceIds = originalServiceIds.filter(
      (id) => !editedServiceIds.includes(id)
    );

    try {
      setIsSaving(true);

      // 1. conceito de espaço
      if (serviceGroup.activity !== group.activity) {
        ensureOk(
          await updateCriteriaGroup(noticeId, groupId, {
            activity: serviceGroup.activity,
          })
        );
      }

      // 2. critérios
      if (addedCriteria.length > 0) {
        ensureOk(
          await addToCriteriaGroup(noticeId, groupId, {
            criteria_ids: addedCriteria,
          })
        );
      }
      if (removedCriteria.length > 0) {
        ensureOk(
          await removeFromCriteriaGroup(noticeId, groupId, {
            criteria_ids: removedCriteria,
          })
        );
      }

      // 3a. serviços existentes → PATCH
      for (let i = 0; i < existingServices.length; i += 1) {
        const service = existingServices[i];
        // eslint-disable-next-line no-await-in-loop
        const res = await updateTypologyGroup(
          service.id,
          buildExistingService(service)
        );
        ensureOk(res);
      }

      // 3b. serviços novos → POST + vincular ao grupo
      if (newServices.length > 0) {
        const response = await createTypologyGroup({
          typology_groups: newServices.map(buildNewService),
        });
        ensureOk(response);
        const savedIds = response.data?.typology_groups_ids ?? [];
        if (savedIds.length !== newServices.length) {
          throw new Error('Algo deu errado, tente novamente.');
        }
        ensureOk(
          await addToCriteriaGroup(noticeId, groupId, {
            typology_group_ids: savedIds,
          })
        );
      }

      // 3c. serviços removidos → DELETE
      for (let i = 0; i < removedServiceIds.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        ensureOk(await deleteTypologyGroup(removedServiceIds[i]));
      }

      setSnackbarMessage('Grupo editado com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      fetchTypologyGroups();
      closeDialog();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (loadingError) closeDialog();
  }, [loadingError]);

  return (
    <>
      <StyledDialog open={open} onClose={() => setConfirmCloseOpen(true)}>
        <DialogContainer>
          <TitleMoldal>
            {IconEditMS} Editar {group.name}
          </TitleMoldal>
          <StyledPaper>
            <Box
              sx={{
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                pt: '12px',
              }}
            >
              <Grid item container spacing={2}>
                <Grid item xs={4}>
                  <SelectTextField
                    required
                    id={`edit-activity-${serviceGroup.id}`}
                    label="conceito de espaço"
                    value={serviceGroup.activity}
                    setValue={(v) =>
                      handleActivityChange(v as NoticeCriteriaGroupActivity)
                    }
                    selectOptions={selectGroupActivity()}
                  />
                </Grid>
              </Grid>

              <Grid item container mt="16px">
                <SubsectionTitle>
                  Critério(s) de aceitação do grupo:
                </SubsectionTitle>
              </Grid>
              <Grid item container>
                {serviceGroup.criteriaIds.map((id) => (
                  <CriteriaItem
                    key={id}
                    criteriaId={id}
                    defaultCriteriasData={criteriasLookup}
                    onDeleteCriteria={handleDeleteCriteria}
                  />
                ))}
                <CriteriasDialog
                  criteriasData={defaultCriterias}
                  selectedCriterias={serviceGroup.criteriaIds}
                  handleSelection={handleCriteriaSelection}
                  loading={loadingCriterias}
                  disable={
                    serviceGroup.activity ===
                      NoticeCriteriaGroupActivity.SELECT ||
                    loadingCriterias ||
                    defaultCriterias.length === 0
                  }
                  activity={serviceGroup.activity}
                  getDefaultCriteriasCallback={getDefaultCriteriasCallback}
                />
              </Grid>

              <Grid item container mt="24px" mb="8px">
                <SubsectionTitle>Serviços:</SubsectionTitle>
              </Grid>
              {serviceGroup.services.map((service, serviceIndex) => (
                <ServiceItem
                  key={service.id}
                  service={service}
                  domId={`edit-service-${service.id}`}
                  serviceLabel={
                    service.code.trim() && service.name.trim()
                      ? `${service.code} - ${service.name}`
                      : `Serviço ${serviceIndex + 1}`
                  }
                  canDeleteService={serviceGroup.services.length > 1}
                  onServiceChange={(field, value) =>
                    handleServiceChange(serviceIndex, field, value)
                  }
                  onTypologyChange={(tIndex, field, value) =>
                    handleTypologyChange(serviceIndex, tIndex, field, value)
                  }
                  onAddTypologyRow={() => handleAddTypologyRow(serviceIndex)}
                  onDeleteTypology={(tIndex) =>
                    handleDeleteTypology(serviceIndex, tIndex)
                  }
                  onDeleteService={() => handleDeleteService(serviceIndex)}
                />
              ))}
              <AddTypologyGroup
                onClick={handleAddService}
                sx={{ marginTop: 1 }}
              >
                {IconAddMS} adicionar serviço
              </AddTypologyGroup>
            </Box>
          </StyledPaper>
          <Grid item container justifyContent="center" mt={2}>
            <Grid item mr={2}>
              <CancelButton onClick={() => setConfirmCloseOpen(true)}>
                Cancelar
              </CancelButton>
            </Grid>
            <Grid item ml={2}>
              <SaveButton onClick={handleSave} disabled={isSaving}>
                {isSaving ? <WhiteCircularProgress size={22} /> : 'Salvar'}
              </SaveButton>
            </Grid>
          </Grid>
        </DialogContainer>
      </StyledDialog>

      {/* Confirmação ao fechar */}
      <Dialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
      >
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            minWidth: 460,
          }}
        >
          <TitleMoldal>Deseja fechar?</TitleMoldal>
          <Typography sx={{ color: '#595959' }}>
            As alterações não salvas serão perdidas.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <CancelButton onClick={() => setConfirmCloseOpen(false)}>
              Não
            </CancelButton>
            <SaveButton
              onClick={() => {
                setConfirmCloseOpen(false);
                closeDialog();
              }}
            >
              Sim
            </SaveButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
